import { Component, computed, inject, output, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SigningAuthenticatorEnrollment } from '@app/models/signing-session/signing-session';
import { SigningAuthenticatorProxyService } from '@app/proxies/signing-authenticator.proxy.service';
import { SigningSessionStateService } from '@app/services/signing-session-state.service';
import { ToastService } from '@app/services/toast.service';
import { finalize } from 'rxjs/operators';

type SetupStep = 'password' | 'scan' | 'done';

const CODE_LENGTH = 6;

@Component({
  selector: 'app-signing-authenticator-setup',
  templateUrl: './signing-authenticator-setup.component.html',
  styleUrl: './signing-authenticator-setup.component.scss',
})
export class SigningAuthenticatorSetupComponent {
  private readonly proxy = inject(SigningAuthenticatorProxyService);
  private readonly state = inject(SigningSessionStateService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);

  /** Emitted when the person chooses to go straight on to a signing session. */
  readonly startSession = output<void>();

  readonly visible = signal(false);
  readonly step = signal<SetupStep>('password');
  readonly busy = signal(false);
  readonly error = signal<string | null>(null);
  readonly enrollment = signal<SigningAuthenticatorEnrollment | null>(null);
  password = '';
  code = '';

  readonly header = computed(() =>
    this.step() === 'done' ? this.translate.instant('Signing_Setup_Header_Done') : this.translate.instant('Signing_Setup_Header'),
  );

  open(): void {
    this.password = '';
    this.code = '';
    this.error.set(null);
    this.enrollment.set(null);
    this.step.set('password');
    this.visible.set(true);
  }

  submitPassword(): void {
    if (!this.password || this.busy()) return;

    this.busy.set(true);
    this.error.set(null);
    this.proxy
      .startEnrollment(this.password)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (response) => {
          this.password = '';
          this.enrollment.set(response.data);
          this.step.set('scan');
        },
        error: (error) => this.error.set(this.state.messageFrom(error)),
      });
  }

  confirmCode(): void {
    if (this.code.length !== CODE_LENGTH || this.busy()) return;

    this.busy.set(true);
    this.error.set(null);
    this.proxy
      .confirmEnrollment(this.code)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          // The secret was only ever needed to load the app; drop it from memory now.
          this.enrollment.set(null);
          this.state.authenticatorConfigured.set(true);
          this.step.set('done');
        },
        error: (error) => {
          this.code = '';
          this.error.set(this.state.messageFrom(error));
        },
      });
  }

  copySecret(): void {
    const secret = this.enrollment()?.secret;
    if (!secret) return;
    navigator.clipboard
      ?.writeText(secret.replace(/\s/g, ''))
      .then(() => this.toast.success(this.translate.instant('Common_Success'), this.translate.instant('Signing_Setup_Copied')));
  }

  goToSession(): void {
    this.visible.set(false);
    this.startSession.emit();
  }

  close(): void {
    this.enrollment.set(null);
    this.visible.set(false);
  }
}
